// Print/PDF document for the curated Project Risk Register. Input is exactly
// the rows currently displayed (same order, same text), never refetched data.
import { T } from "../../../app/tokens.js";
import { escHtml, openPrintWindow, POPUP_BLOCKED_MESSAGE } from "../../reports/wprPrint.js";

export const RISK_LEVEL_META = {
  high: { label: "High", fg: T.ragRed, bg: T.ragRedBg, border: T.ragRedBorder },
  med: { label: "Med", fg: T.ragAmberText, bg: T.ragAmberBg, border: T.ragAmberBorder },
  low: { label: "Low", fg: T.ragGreenText, bg: T.ragGreenBg, border: T.ragGreenBorder },
};
export const NOT_SET_META = { label: "Not set", fg: T.mid, bg: T.lightGray, border: T.border };

export function levelMeta(level) {
  return RISK_LEVEL_META[level] || NOT_SET_META;
}

/** Counts rows (projects) per level; null levels count as notSet. */
export function countLevels(rows) {
  const c = { high: 0, med: 0, low: 0, notSet: 0 };
  for (const r of rows || []) {
    if (RISK_LEVEL_META[r.level]) c[r.level] += 1; else c.notSet += 1;
  }
  return c;
}

function chip(level) {
  const m = levelMeta(level);
  return `<span class="chip" style="background:${m.bg};color:${m.fg};border-color:${m.border};">${escHtml(m.label)}</span>`;
}

/**
 * rows: [{ name, location, level, items: [string], provenance: string }]
 * latest: provenance string or null
 */
export function buildRiskPrintHtml({ rows, latest, generatedAt }) {
  const list = Array.isArray(rows) ? rows : [];
  const c = countLevels(list);
  const body = list.length === 0
    ? `<tr><td colspan="3" class="muted">No projects to show.</td></tr>`
    : list.map((r) => {
        const items = (r.items || []).length === 0
          ? `<span class="muted">No risks to report</span>`
          : `<ul>${r.items.map((t) => `<li>${escHtml(t)}</li>`).join("")}</ul>`;
        return `<tr><td><div class="pn">${escHtml(r.name)}</div>${r.location ? `<div class="muted">${escHtml(r.location)}</div>` : ""}</td><td>${chip(r.level)}</td><td>${items}<div class="prov">${escHtml(r.provenance)}</div></td></tr>`;
      }).join("");
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Risk Register</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: ${T.dark}; background: ${T.card}; padding: 28px 36px; font-size: 12px; }
    h1 { font-size: 20px; font-weight: 800; color: ${T.projectsHubNavy}; }
    .sub { color: ${T.mid}; margin: 3px 0 12px; }
    .sum { margin-bottom: 14px; }
    .sp { display:inline-block; border:1px solid ${T.border}; border-radius:999px; padding:2px 10px; margin-right:6px; font-weight:700; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.04em; color:${T.mid}; border-bottom:2px solid ${T.borderDark}; padding:8px 10px; }
    td { padding: 10px; border-bottom: 1px solid ${T.border}; vertical-align: top; page-break-inside: avoid; }
    .pn { font-weight: 700; }
    .chip { display:inline-block; border:1px solid; border-radius:6px; padding:1px 9px; font-weight:700; font-size:11px; }
    ul { margin-left: 16px; } li { margin: 2px 0; line-height: 1.45; }
    .muted { color: ${T.mid}; }
    .prov { color: ${T.light}; font-size: 10px; margin-top: 4px; }
    @media print { body { padding: 12px 18px; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <h1>Risk Register</h1>
  <p class="sub">${list.length} ${list.length === 1 ? "project" : "projects"}${latest ? ` &middot; ${escHtml(latest)}` : ""}${generatedAt ? ` &middot; Printed ${escHtml(generatedAt)}` : ""}</p>
  <div class="sum"><span class="sp" style="color:${T.ragRed}">High ${c.high}</span><span class="sp" style="color:${T.ragAmberText}">Med ${c.med}</span><span class="sp" style="color:${T.ragGreenText}">Low ${c.low}</span>${c.notSet ? `<span class="sp" style="color:${T.mid}">Not set ${c.notSet}</span>` : ""}</div>
  <table><thead><tr><th style="width:26%">Project</th><th style="width:12%">Risk</th><th>Details</th></tr></thead><tbody>${body}</tbody></table>
  <script>window.onload = function () { window.print(); };<\/script>
</body>
</html>`;
}

/** Opens synchronously. Returns null on success, or a user-facing error message. */
export function printRiskRegister(args, win) {
  return openPrintWindow(buildRiskPrintHtml(args), win) ? null : POPUP_BLOCKED_MESSAGE;
}
